import { useEffect, useState } from 'react';
import { Avatar, Box, Chip, Pagination, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { TextAdItem } from '../types';
import { useLocales } from 'src/locales';
import { titleCase } from 'src/utils/change-case';
import { domainFavicon } from '../utils';
import SerpModalButton from './SerpModalButton';

interface Props {
    items: TextAdItem[];
    pageSize?: number;
    showBrandChip?: boolean;
}

export default function TextAdsList({ items, pageSize = 8, showBrandChip = false }: Props) {
    const [page, setPage] = useState(1);
    useEffect(() => { setPage(1); }, [items]);
    const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
    const start = (page - 1) * pageSize;
    const current = items.slice(start, start + pageSize);
    const { t } = useLocales();
    return (
        <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2">{titleCase(t("dashboard.global.competitiveIntelligence.text_ads"))}</Typography>
                </Stack>
                <Typography variant="caption" fontSize={14} color="text.secondary">{items.length} {t("dashboard.global.competitiveIntelligence.results_found")}</Typography>
            </Stack>
            {items.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.neutral' }}>
                    <Typography variant="body2" color="text.secondary">{titleCase(t("dashboard.global.competitiveIntelligence.no_txt_ads_found_for_this_country"))}</Typography>
                </Paper>
            ) : (
                <Stack spacing={1}>
                    {current.map((ad, idx) => (
                        <Paper key={idx} variant="outlined" sx={{ p: 1.5, backgroundColor: 'background.neutral' }}>
                            <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Avatar src={domainFavicon(ad.domain)} sx={{ width: 20, height: 20, backgroundColor: '#fff' }} />
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>{ad.domain}</Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <SerpModalButton
                                            gcsUrl={(ad as any)._gcs_uri_mobile}
                                            deviceType={ad.device_type}
                                            iconType="mobile"
                                            searchTerm={ad.searchTerm}
                                        />
                                        <SerpModalButton
                                            gcsUrl={(ad as any)._gcs_uri_desktop}
                                            deviceType={ad.device_type}
                                            iconType="desktop"
                                            searchTerm={ad.searchTerm}
                                        />
                                        <Tooltip arrow placement="top" title={t('definitions.SERP')}>
                                          <Typography variant="caption" sx={{ opacity: 0.8 }}>{t('dashboard.global.competitiveIntelligence.Serp')}</Typography>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {ad.title}
                                    {showBrandChip && (ad as any).brandInfringementTitle && (
                                        <Chip label="Brand" color="error" size="small" sx={{ height: 18 }} />
                                    )}
                                </Typography>
                                <Typography variant="body2" placement="top" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {ad.description}
                                    {showBrandChip && (ad as any).brandInfringementDescription && (
                                        <Chip label="Brand" color="error" size="small" sx={{ height: 18 }} />
                                    )}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Tooltip arrow placement="top" title={t('definitions.position')}>
                                          <Typography variant="caption" sx={{ opacity: 0.6, fontSize: 13 }}>{t('dashboard.global.competitiveIntelligence.Position')}</Typography>
                                        </Tooltip>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 1, fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
                                            {`#${ad.position}`}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.6, fontSize: 13 }}>|</Typography>
                                        <Tooltip placement="top" arrow title={t('definitions.frequency')}>
                                          <Typography variant="caption" sx={{ opacity: 0.6, fontSize: 13 }}>{t('dashboard.global.competitiveIntelligence.frequency')}</Typography>
                                        </Tooltip>
                                        <Typography variant="caption" sx={{ opacity: 1, fontSize: 14, fontWeight: 600, color: 'text.primary' }}>{`${ad.frequency}%`}</Typography>
                                    </Box>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                    {pageCount > 1 && (
                        <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
                            <Pagination color="standard" size="small" count={pageCount} page={page} onChange={(_, p) => setPage(p)} />
                        </Stack>
                    )}
                </Stack>
            )}
        </Stack>
    );
}


